<!-- This is meant to be a Popover replacement that hides rather than teleports, so that its content can be accessed by document queries -->
<template>
  <div v-bind="$attrs" ref="contentRef" v-show="visible" :style="positionStyle">
    <slot></slot>
  </div>
</template>

<script setup>
  import { ref, onMounted, onBeforeUnmount, nextTick, watch } from 'vue';

  // Props definition
  const props = defineProps({
    modelValue:   { type: Boolean, default: false },
    dismissable:  { type: Boolean, default: true }
  });

  // Emits
  const emit = defineEmits(['update:modelValue']);

  // Refs and state
  const visible = ref(props.modelValue);
  const contentRef = ref(null);
  const positionStyle = ref({});
  const currentTarget = ref(null);

  const calculatePosition = (eventOrElement) => {
    const targetElement = eventOrElement instanceof Event 
      ? eventOrElement.currentTarget || eventOrElement.target
      : eventOrElement;

    if (!targetElement || !contentRef.value) return {};

    currentTarget.value = targetElement;

    const targetRect = targetElement.getBoundingClientRect();
    const contentElement = contentRef.value;

    // Temporarily position off-screen to measure
    contentElement.style.position = "fixed";
    contentElement.style.opacity = "0";
    contentElement.style.display = "block";

    const contentRect = contentElement.getBoundingClientRect();
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;

    // Calculate initial position using offsetTop to prevent misalignment
    let parentOffset = targetElement.offsetParent ? targetElement.offsetParent.offsetTop : 0;
    let top = targetElement.offsetTop + targetElement.offsetHeight + 5;
    let left = targetElement.left;

    // Adjust horizontal position if overflowing
    if (left + contentRect.width > windowWidth) {
      left = Math.max(targetRect.right - contentRect.width, 5);
    }

    // Adjust vertical position if overflowing
    if (top + contentRect.height > windowHeight) {
      const aboveTop = targetElement.offsetTop - contentRect.height - 5;
      top = aboveTop > 5 ? aboveTop : top;
    }

    // Apply final position
    contentElement.style.position = "absolute";
    contentElement.style.top = `${top}px`;
    contentElement.style.left = `${left}px`;
    contentElement.style.opacity = "";

    return {
      position: "absolute",
      top: `${top}px`,
      left: `${left}px`
    };
  };

  const preventEventDefaults = (event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  const show = (event) => {
    preventEventDefaults(event);

    if (!visible.value) {
      positionStyle.value = calculatePosition(event);
      visible.value = true;
      emit('update:modelValue', true);
    }
  };

  // Update position
  const updatePosition = () => {
    if (visible.value) {
      nextTick(() => {
        positionStyle.value = calculatePosition(currentTarget.value);
      });
    }
  };

  // Toggle visibility
  const toggle = (event) => {
    preventEventDefaults(event);

    visible.value = !visible.value;
    emit('update:modelValue', visible.value);

    if (visible.value && event) {
      positionStyle.value = calculatePosition(event);
    }
  };

  // Hide popover
  const hide = () => {
    if (visible.value) {
      visible.value = false;
      emit('update:modelValue', false);
    }
  };

  // Watch for external modelValue changes
  watch(() => props.modelValue, (newValue) => {
    if (newValue !== visible.value) {
      visible.value = newValue;
      if (visible.value) {
        updatePosition();
      }
    }
  });

  // Handle window resize
  const handleResize = () => {
    updatePosition();
  };

  // Handle click outside to close popover
  const handleClickOutside = (event) => {
    if (props.dismissable &&
        visible.value &&
        contentRef.value &&
        !contentRef.value.contains(event.target) &&
        currentTarget.value &&
        !currentTarget.value.contains(event.target)) {
      hide();
    }
  };

  // Setup event listeners
  onMounted(() => {
    if (props.dismissable) {
      document.addEventListener('click', handleClickOutside, true);
    }
    window.addEventListener('resize', handleResize);
  });

  onBeforeUnmount(() => {
    document.removeEventListener('click', handleClickOutside, true);
    window.removeEventListener('resize', handleResize);
  });

  // Expose methods to parent component
  defineExpose({
    toggle,
    hide,
    show,
    updatePosition
  });
</script>
